'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { useSearchParams } from 'next/navigation';
import { useCallback, useRef, useState } from 'react';
import { SearchTopAdBanner } from '@/app/_components/ads';
import { usePageSearchBarVisibility, useSearchHistoryStore } from '@/app/_components/SearchBar';
import { DEFAULT_DONATION_SEARCH_CATEGORY, DEFAULT_SEARCH_CATEGORY, isDonationSearchCategory, isSearchCategory, SEARCH_CATEGORY_QUERY_KEY, type SearchCategory, type SearchScope } from '@/app/_components/SearchBar/constants';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAppBack } from '@/modules/wello-app/_hooks/useAppBack';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { AllSearch } from './_components/AllSearch';
import { DonationBusinessSearch } from './_components/DonationBusinessSearch';
import { FestivalSearch } from './_components/FestivalSearch/FestivalSearch';
import { GiftSearch } from './_components/GiftSearch';
import { HometownNewsSearch } from './_components/HometownNewsSearch';
import { LocalMarketSearch } from './_components/LocalMarketSearch';
import { PolicyInfoSearch } from './_components/PolicyInfoSearch';
import { PolicySearch } from './_components/PolicySearch';
import { SearchPageHeader } from './_components/SearchPageHeader';
import { useIntegratedSearchFilter } from './_hooks/useIntegratedSearchFilter';
import { usePolicyFilter } from './_hooks/usePolicyFilter';
import { UrlFilterControllerProvider, useCategoryFilterCount } from './_hooks/useUrlFilterController';
import { SECTION_HEADER_MAX_W } from './constants';
const resolveCategory = (categoryParam: string | null, scope?: SearchScope): SearchCategory => {
  if (scope === 'donation') {
    return isDonationSearchCategory(categoryParam) ? categoryParam : DEFAULT_DONATION_SEARCH_CATEGORY;
  }
  return isSearchCategory(categoryParam) ? categoryParam : DEFAULT_SEARCH_CATEGORY;
};
export function SearchPageView(t0) {
  const $ = _c(2);
  const {
    scope
  } = t0;
  let t1;
  if ($[0] !== scope) {
    t1 = <UrlFilterControllerProvider><SearchPageContent scope={scope} /></UrlFilterControllerProvider>;
    $[0] = scope;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function SearchPageContent(t0) {
  const $ = _c(76);
  const {
    scope
  } = t0;
  useAppBack();
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const searchParams = useSearchParams();
  const resultsSectionRef = useRef(null);
  const {
    state,
    setKeyword,
    toggleTargetCode,
    toggleProvisionCode,
    toggleJobCode,
    toggleAgeCode,
    resetFilters,
    setSort,
    setPage,
    activeFilterCount
  } = usePolicyFilter();
  const {
    state: integratedState,
    setRegionCode,
    setSubRegionCode,
    toggleGiftCategory,
    toggleGiftInstitution,
    toggleDonationInstitution,
    setDonationSort,
    setFestivalLclsSystm2,
    setFestivalRegionCode,
    setFestivalSubRegionCode,
    setFestivalRadius,
    setFestivalMonth,
    resetHometownNewsFilters,
    resetGiftFilters,
    resetDonationFilters,
    resetFestivalFilters,
    hometownNewsFilterCount,
    giftFilterCount,
    donationFilterCount,
    festivalFilterCount
  } = useIntegratedSearchFilter();
  const policyInfoFilterCount = useCategoryFilterCount("policy-info");
  const searchHistory = useSearchHistoryStore(_temp);
  const add = useSearchHistoryStore(_temp2);
  const remove = useSearchHistoryStore(_temp3);
  const clear = useSearchHistoryStore(_temp4);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const {
    ref: searchBarRef
  } = usePageSearchBarVisibility();
  let t1;
  if ($[0] !== scope || $[1] !== searchParams) {
    const categoryParam = searchParams.get(SEARCH_CATEGORY_QUERY_KEY);
    t1 = resolveCategory(categoryParam, scope);
    $[0] = scope;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const category = t1;
  const isAllCategory = category === "all";
  const isPolicyCategory = category === "policy";
  let t2;
  if ($[3] !== add || $[4] !== category || $[5] !== setKeyword) {
    t2 = keyword => {
      setKeyword(keyword);
      add(keyword);
      if (keyword && category !== "all") {
        trackGtmEvent(GTM_EVENT_TYPE.SEARCH, {
          search_type: "\uD1B5\uD569\uAC80\uC0C9",
          search_term: keyword,
          search_category: category
        });
      }
    };
    $[3] = add;
    $[4] = category;
    $[5] = setKeyword;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleSearch = t2;
  let t3;
  if ($[7] !== category || $[8] !== donationFilterCount || $[9] !== festivalFilterCount || $[10] !== giftFilterCount || $[11] !== hometownNewsFilterCount || $[12] !== policyInfoFilterCount) {
    const getIntegratedFilterCount = function getIntegratedFilterCount() {
      switch (category) {
        case "hometown-news":
          {
            return hometownNewsFilterCount;
          }
        case "policy-info":
          {
            return policyInfoFilterCount;
          }
        case "gifts":
          {
            return giftFilterCount;
          }
        case "hometown-business":
          {
            return donationFilterCount;
          }
        case "festival":
          {
            return festivalFilterCount;
          }
        default:
          {
            return 0;
          }
      }
    };
    t3 = getIntegratedFilterCount();
    $[7] = category;
    $[8] = donationFilterCount;
    $[9] = festivalFilterCount;
    $[10] = giftFilterCount;
    $[11] = hometownNewsFilterCount;
    $[12] = policyInfoFilterCount;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const integratedFilterCount = t3;
  const hasIntegratedFilter = category === "hometown-news" || category === "policy-info" || category === "gifts" || category === "hometown-business" || category === "festival";
  const filterCount = isAllCategory ? 0 : isPolicyCategory ? activeFilterCount : integratedFilterCount;
  let t4;
  if ($[14] !== hasIntegratedFilter || $[15] !== isAllCategory || $[16] !== isPolicyCategory) {
    t4 = !isAllCategory && (isPolicyCategory || hasIntegratedFilter) ? () => setIsFilterOpen(true) : undefined;
    $[14] = hasIntegratedFilter;
    $[15] = isAllCategory;
    $[16] = isPolicyCategory;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const onFilterClick = t4;
  let t5;
  if ($[18] !== isDesktopViewport) {
    t5 = () => {
      const resultsSection = resultsSectionRef.current;
      if (!resultsSection) {
        return;
      }
      const top = resultsSection.getBoundingClientRect().top + window.scrollY - (isDesktopViewport ? 24 : 56);
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: "auto"
      });
    };
    $[18] = isDesktopViewport;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const scrollToResultsTop = t5;
  let t6;
  if ($[20] !== scrollToResultsTop || $[21] !== setPage) {
    t6 = page => {
      setPage(page);
      scrollToResultsTop();
    };
    $[20] = scrollToResultsTop;
    $[21] = setPage;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const handlePageChange = t6;
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsFilterOpen(false);
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const closeFilter = t7;
  let t8;
  if ($[24] !== category || $[25] !== handlePageChange || $[26] !== integratedState || $[27] !== isFilterOpen || $[28] !== resetDonationFilters || $[29] !== resetFestivalFilters || $[30] !== resetFilters || $[31] !== resetGiftFilters || $[32] !== resetHometownNewsFilters || $[33] !== setDonationSort || $[34] !== setFestivalLclsSystm2 || $[35] !== setFestivalMonth || $[36] !== setFestivalRadius || $[37] !== setFestivalRegionCode || $[38] !== setFestivalSubRegionCode || $[39] !== setRegionCode || $[40] !== setSort || $[41] !== setSubRegionCode || $[42] !== state || $[43] !== toggleAgeCode || $[44] !== toggleDonationInstitution || $[45] !== toggleGiftCategory || $[46] !== toggleGiftInstitution || $[47] !== toggleJobCode || $[48] !== toggleProvisionCode || $[49] !== toggleTargetCode) {
    t8 = () => {
      switch (category) {
        case "all":
          {
            return <AllSearch keyword={state.keyword} />;
          }
        case "policy":
          {
            return <PolicySearch isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} state={state} onToggleTargetCode={toggleTargetCode} onToggleProvisionCode={toggleProvisionCode} onToggleJobCode={toggleJobCode} onToggleAgeCode={toggleAgeCode} onResetFilters={resetFilters} onSortChange={setSort} onPageChange={handlePageChange} />;
          }
        case "hometown-news":
          {
            return <HometownNewsSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} regionCode={integratedState.regionCode} subRegionCode={integratedState.subRegionCode} onRegionChange={setRegionCode} onSubRegionChange={setSubRegionCode} onReset={resetHometownNewsFilters} />;
          }
        case "festival":
          {
            return <FestivalSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} lclsSystm2={integratedState.festivalLclsSystm2} onSelectLclsSystm2={setFestivalLclsSystm2} regionCode={integratedState.festivalRegionCode} subRegionCode={integratedState.festivalSubRegionCode} onSelectRegion={setFestivalRegionCode} onSelectSubRegion={setFestivalSubRegionCode} radius={integratedState.festivalRadius} onSelectRadius={setFestivalRadius} month={integratedState.festivalMonth} onSelectMonth={setFestivalMonth} onReset={resetFestivalFilters} />;
          }
        case "policy-info":
          {
            return <PolicyInfoSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} />;
          }
        case "gifts":
          {
            return <GiftSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} categoryIds={integratedState.giftCategoryIds} institutionIds={integratedState.giftInstitutionIds} onToggleCategory={toggleGiftCategory} onToggleInstitution={toggleGiftInstitution} onReset={resetGiftFilters} />;
          }
        case "hometown-business":
          {
            return <DonationBusinessSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} isFilterOpen={isFilterOpen} onCloseFilter={closeFilter} institutionIds={integratedState.donationInstitutionIds} sort={integratedState.donationSort} onToggleInstitution={toggleDonationInstitution} onSortChange={setDonationSort} onReset={resetDonationFilters} />;
          }
        case "welmarket":
          {
            return <LocalMarketSearch keyword={state.keyword} page={state.page} onPageChange={handlePageChange} />;
          }
      }
    };
    $[24] = category;
    $[25] = handlePageChange;
    $[26] = integratedState;
    $[27] = isFilterOpen;
    $[28] = resetDonationFilters;
    $[29] = resetFestivalFilters;
    $[30] = resetFilters;
    $[31] = resetGiftFilters;
    $[32] = resetHometownNewsFilters;
    $[33] = setDonationSort;
    $[34] = setFestivalLclsSystm2;
    $[35] = setFestivalMonth;
    $[36] = setFestivalRadius;
    $[37] = setFestivalRegionCode;
    $[38] = setFestivalSubRegionCode;
    $[39] = setRegionCode;
    $[40] = setSort;
    $[41] = setSubRegionCode;
    $[42] = state;
    $[43] = toggleAgeCode;
    $[44] = toggleDonationInstitution;
    $[45] = toggleGiftCategory;
    $[46] = toggleGiftInstitution;
    $[47] = toggleJobCode;
    $[48] = toggleProvisionCode;
    $[49] = toggleTargetCode;
    $[50] = t8;
  } else {
    t8 = $[50];
  }
  const renderSection = t8;
  const t9 = SECTION_HEADER_MAX_W[category];
  let t10;
  if ($[51] !== t9) {
    t10 = cn("w-full max-w-screen-xl", t9);
    $[51] = t9;
    $[52] = t10;
  } else {
    t10 = $[52];
  }
  let t11;
  if ($[53] !== searchHistory) {
    t11 = searchHistory ?? [];
    $[53] = searchHistory;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  let t12;
  if ($[55] !== clear || $[56] !== filterCount || $[57] !== handleSearch || $[58] !== onFilterClick || $[59] !== remove || $[60] !== scope || $[61] !== searchBarRef || $[62] !== state.keyword || $[63] !== t11) {
    t12 = <SearchPageHeader ref={searchBarRef} scope={scope} keyword={state.keyword} filterCount={filterCount} recentKeywords={t11} onSearch={handleSearch} onFilterClick={onFilterClick} onSelectRecent={handleSearch} onRemoveRecent={remove} onClearRecent={clear} />;
    $[55] = clear;
    $[56] = filterCount;
    $[57] = handleSearch;
    $[58] = onFilterClick;
    $[59] = remove;
    $[60] = scope;
    $[61] = searchBarRef;
    $[62] = state.keyword;
    $[63] = t11;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  let t13;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mt-12 mobile-tablet:mt-0 mobile-tablet:px-16 mobile-tablet:py-12"><SearchTopAdBanner /></div>;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  let t14;
  if ($[66] !== t10 || $[67] !== t12) {
    t14 = <div className={t10}>{t12}{t13}</div>;
    $[66] = t10;
    $[67] = t12;
    $[68] = t14;
  } else {
    t14 = $[68];
  }
  let t15;
  if ($[69] !== renderSection) {
    t15 = renderSection();
    $[69] = renderSection;
    $[70] = t15;
  } else {
    t15 = $[70];
  }
  let t16;
  if ($[71] !== t15) {
    t16 = <div ref={resultsSectionRef} className="w-full">{t15}</div>;
    $[71] = t15;
    $[72] = t16;
  } else {
    t16 = $[72];
  }
  let t17;
  if ($[73] !== t14 || $[74] !== t16) {
    t17 = <div className="flex w-full flex-col items-center gap-12 p-40 mobile-tablet:gap-0 mobile-tablet:p-0">{t14}{t16}</div>;
    $[73] = t14;
    $[74] = t16;
    $[75] = t17;
  } else {
    t17 = $[75];
  }
  return t17;
}
function _temp4(state_3) {
  return state_3.clear;
}
function _temp3(state_2) {
  return state_2.remove;
}
function _temp2(state_1) {
  return state_1.add;
}
function _temp(state_0) {
  return state_0.data;
}